import * as React from 'react';
import { useHistory } from 'react-router';
import { Authority } from '../components/AuthorityLabel';
import { Button } from '../components/Button';
import {
  ConfirmDialog,
  GraphQLErrorDialog,
} from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { PageHeader } from '../components/PageHeader';
import { ToastContext } from '../components/Toast';
import {
  useDeleteShapeTileMutation,
  useGetConfigLazyQuery,
  useUpdateShapeTilesDisplayOrderMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import './ShapeTileListPage.css';
import { TableScroll } from '../components/TableScroll';
import { orderByIds } from '../utils/reorder';
import {
  getReorderTableStorageKey,
  getTableColumnCount,
  ReorderHandleCell,
  ReorderHandleHeader,
  useListReorder,
} from '../components/ReorderMode';
import { ListSummary } from '../components/ListControls';
import { LIST_SEARCH_EMPTY_MESSAGE, useListSearch } from '../components/ListSearch';

interface DeleteShapeTileTarget {
  id: number;
  name: string;
}

/**
 * 表の列数。0件の行を表の幅いっぱいに広げるために使う。
 * 並びを変えられる人には左端に取っ手の列が加わるので、0件の行の幅は getTableColumnCount で数える
 */
const COLUMN_COUNT = 5;

/**
 * タイルの編集に必要な権限の段階。
 * この一覧の編集・削除は、共通設定（/configs配下）の画面を開けるマスター編集者以上に出しており、
 * サーバーの並び順の保存（updateShapeTilesDisplayOrder）もマスター編集者以上なので、「並び替え」も同じ段階にする
 */
const EDIT_AUTHORITY_LEVEL = Authority.MasterAdmin;

/**
 * タイルの一覧
 */
export const ShapeTileListPage: React.FC = function () {
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const [deleteTarget, setDeleteTarget] = React.useState<DeleteShapeTileTarget>();
  const [getConfig, getConfigResult] = useGetConfigLazyQuery();
  const [deleteShapeTile, deleteShapeTileResult] = useDeleteShapeTileMutation({
    onCompleted() {
      toastContext.showToast('削除しました', 'success');
      getConfig();
    },
    onError() {
      // 削除に失敗しても一覧は残し、続けて操作できるようにする
      toastContext.showToast('削除できませんでした。時間をおいて再実行してください。', 'error');
    },
  });
  // 見出しの右の絞り込みの欄。絞り込んでいる間は並びを変えさせない
  const search = useListSearch();
  const [updateDisplayOrder] = useUpdateShapeTilesDisplayOrderMutation();
  // データを読み込む前は空の並びを渡し、読み込んだ後はサーバーが返した並び順を渡す。
  // 取っ手を落とすとすぐに保存し、保存の通知と「元に戻す」はこのフックが出す
  const reorderMode = useListReorder((getConfigResult.data?.getConfig.tiles ?? []).map((tile) => tile.id), {
    authLv: EDIT_AUTHORITY_LEVEL,
    filtering: search.filtering,
    save: (ids) => updateDisplayOrder({ variables: { shapeTileIds: ids } }),
  });

  if (!getConfigResult.called) {
    getConfig();
  }

  if (getConfigResult.error) {
    return <GraphQLErrorDialog error={getConfigResult.error} />;
  }

  if (
    getConfigResult.loading ||
    !getConfigResult.data ||
    deleteShapeTileResult.loading
  ) {
    return <Loading />;
  }

  const tiles = getConfigResult.data.getConfig.tiles;
  const displayedTiles = orderByIds(tiles, reorderMode.displayedIds).filter((tile) =>
    search.matches([tile.name])
  );

  let rows;
  if (displayedTiles.length === 0) {
    // 1件も登録されていないときと、絞り込みで1件も残らなかったときで、出す文を分ける
    let emptyMessage = LIST_SEARCH_EMPTY_MESSAGE;
    if (tiles.length === 0) {
      emptyMessage = 'タイルが登録されていません';
    }
    rows = <EmptyRow colSpan={getTableColumnCount(reorderMode, COLUMN_COUNT)} message={emptyMessage} />;
  } else {
    rows = displayedTiles.map((tile, index) => {
      // 並びを変えられる人には、行の左端に取っ手の列を置く。操作の列（編集・削除）はいつも出す
      let handleCell;
      if (reorderMode.handleColumn) {
        handleCell = <ReorderHandleCell {...reorderMode.getHandleCellProps(tile.id, index)} />;
      }
      const actionCell = (
        <td className="App-table_button_cell">
          <div className="App-table_actions">
            <Button
              mini={true}
              color="white"
              onClick={() => history.push(`/configs/shape_tiles/${tile.id}/edit`)}
            >
              編集
            </Button>
            <Button mini={true} color="red" onClick={() => setDeleteTarget(tile)}>
              削除
            </Button>
          </div>
        </td>
      );

      return (
        <tr key={tile.id} {...reorderMode.getRowProps(tile.id)}>
          {handleCell}
          <td>{tile.name}</td>
          <td>{tile.width}</td>
          <td>{tile.height}</td>
          <td>{tile.margin}</td>
          {actionCell}
        </tr>
      );
    });
  }

  let deleteDialog;
  if (deleteTarget) {
    deleteDialog = (
      <ConfirmDialog
        open={true}
        danger={true}
        title="タイルの削除"
        message={<p>{deleteTarget.name}を削除しますか？</p>}
        yesLabel="削除"
        onYes={() => {
          deleteShapeTile({ variables: { shapeTileId: deleteTarget.id } });
          setDeleteTarget(undefined);
        }}
        onNo={() => setDeleteTarget(undefined)}
      />
    );
  }

  // 見出しの右のボタン
  const headerActions = (
    <>
      <Button
        mini={true}
        onClick={() => history.push('/configs/shape_tiles/new')}
      >
        新規作成
      </Button>
    </>
  );

  // 並びを変えられる人には、取っ手の列を左端に置き、見出しは空にして幅を変えられないようにする
  let handleHeader;
  if (reorderMode.handleColumn) {
    handleHeader = <ReorderHandleHeader />;
  }
  const actionHeader = <th className="App-table_actions_column">操作</th>;

  const tableStorageKey = getReorderTableStorageKey(reorderMode, 'shapeTiles');

  return (
    <div className="ShapeTileListPage">
      {deleteDialog}
      <PageHeader
        title="タイル設定"
        breadcrumbs={[{ label: '共通設定' }, { label: 'タイル設定' }]}
        actions={headerActions}
      />
      <ListSummary
        reorder={reorderMode}
        shownCount={displayedTiles.length}
        search={{ search, placeholder: '名前で絞り込み' }}
      />
      {/* 取っ手の列があるかどうかで列の並びが違うので、列の幅は別々に保存する */}
      <TableScroll key={tableStorageKey} storageKey={tableStorageKey}>
        <table className="App-table App-table_full">
          <thead>
            <tr>
              {handleHeader}
              <th>名前</th>
              <th>幅</th>
              <th>高さ</th>
              <th>間隔</th>
              {actionHeader}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
